import styled from 'styled-components';
import { isTruthy } from '@ardoq/common-helpers';
import { colors, s16, s32, s8 } from '@ardoq/design-tokens';
import { InlineText } from '@ardoq/typography';
import {
  ContextDashboardIsUsedIn,
  DashboardModes,
  DashboardProps,
  OnWidgetDrilldown,
  TrackEvent,
} from './types';
import { DashboardLoadingOverlay } from './DashboardLoadingOverlay';
import { WidgetsContainer } from './widgets/atoms/WidgetsContainer';
import { ChartWidget } from './widgets/ChartWidget';
import { HeaderWidget } from './widgets/HeaderWidget/HeaderWidget';
import {
  DASHBOARD_MAX_SCREEN_WIDTH,
  DASHBOARD_MAX_WIDTH,
  DASHBOARD_MIN_SCREEN_WIDTH,
} from './consts';
import { DashboardTrackingEventsNames } from './tracking';
import { DrilldownFromLocations, trackDrilldown } from '@ardoq/report-reader';
import { formatDateShort, formatDateTimeShort } from '@ardoq/date-time';
import { DashboardErrorPage } from './ErrorPage';
import { EmptyDashboard } from './EmptyDashboard';
import { Icon, IconName } from '@ardoq/icons';
import { Locale } from '@ardoq/locale';
import { PageHeaderIsland, PageHeaderIslandProps } from '@ardoq/page-layout';
import { useRef } from 'react';
import { FullscreenWidgetModal } from './widgets/FullscreenWidgetModal/FullscreenWidgetModal';
import { NoteWidget } from './widgets/NoteWidget/NoteWidget';
import { PowerBiIframeWidget } from './widgets/PowerBiIframeWidget/PowerBiIframeWidget';
import {
  ActionableNewFeatureNotification,
  CopyEntityURLButton,
  CopyEntityURLButtonProps,
} from '@ardoq/snowflakes';
import { NotificationWrapper } from '@ardoq/status-ui';
import { APIDashboardAttributes } from '@ardoq/api-types';
import { DashboardFilterReaderIsland } from './filter/DashboardFilterIsland';
import {
  isChartProps,
  isHeaderProps,
  isPowerBiIframeProps,
  isNoteProps,
} from './widgets/utils';
import { FlexBox } from '@ardoq/layout';
import { SecondaryButton } from '@ardoq/button';
import { getSortedWidgetPropsArray } from './utils';
import { type I18nLocale, t, type WithI18nLocale } from '@ardoq/i18n';
import { ChartAnimationDisabledContext } from './widgets/categoricalCharts/chartAnimation';
import { captureModeControlStyles } from '@ardoq/export/captureMode';

const Container = styled(FlexBox).attrs({
  flexDirection: 'column',
})<{ $isCaptureMode: boolean }>`
  overflow: ${({ $isCaptureMode }) => ($isCaptureMode ? 'visible' : 'auto')};
  width: 100%;
  height: 100%;
  /* stylelint-disable-next-line media-query-no-invalid */
  @media (min-width: ${DASHBOARD_MAX_SCREEN_WIDTH}px) {
    align-items: center;
  }
  /* stylelint-disable-next-line media-query-no-invalid */
  @media (max-width: ${DASHBOARD_MIN_SCREEN_WIDTH}px) {
    padding: 0 ${s16} ${s16} ${s16};
  }
  padding: 0 ${s32} ${s32} ${s32};
  ${({ $isCaptureMode }) => $isCaptureMode && captureModeControlStyles}
`;

const TimeStampContainer = styled.div`
  color: ${colors.textSubtle};
  min-height: ${s32};
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const MetricsDateRangeContainer = styled.span`
  display: flex;
  gap: ${s8};
  align-items: center;
`;

interface MetricsDateRangeTitleProps {
  name: string;
  periodStart: Date;
  periodEnd: Date;
  locale: Locale;
}

const MetricsDateRangeTitle = ({
  name,
  periodStart,
  periodEnd,
  locale,
}: MetricsDateRangeTitleProps) => {
  return (
    <MetricsDateRangeContainer>
      <span>{name}</span>
      <Icon iconName={IconName.CALENDAR} />
      <InlineText variant="text1">
        {formatDateShort(periodStart, locale)} -{' '}
        {formatDateShort(periodEnd, locale)}
      </InlineText>
    </MetricsDateRangeContainer>
  );
};

interface TimeStampProps {
  time?: Date;
  locale: Locale;
  i18nLocale: I18nLocale;
}

const TimeStamp = ({ time, locale, i18nLocale }: TimeStampProps) => (
  <TimeStampContainer>
    {time
      ? t(
          i18nLocale,
          'dashboard:dashboard.dataUpdatedAt',
          { time: formatDateTimeShort(time, locale) },
          'Dashboard data has been updated at: {{time}}'
        )
      : ''}
  </TimeStampContainer>
);

export const getPageHeaderIslandProps = ({
  dashboardMode,
  dashboardData,
  contextDashboardIsUsedIn,
  copyUrlButtonProps,
  trackEvent,
  downloadButtonProps,
  isCaptureMode,
}: {
  dashboardData: APIDashboardAttributes & { _time: Date };
  dashboardMode: DashboardModes;
  userLocale: Locale;
  contextDashboardIsUsedIn: ContextDashboardIsUsedIn;
  copyUrlButtonProps?: CopyEntityURLButtonProps;
  trackEvent: TrackEvent;
  downloadButtonProps?: { label: string; onClick: VoidFunction };
  isCaptureMode: boolean;
}): Omit<PageHeaderIslandProps, 'title'> => {
  const showCopyURLButton =
    dashboardMode === DashboardModes.VIEW &&
    contextDashboardIsUsedIn !== 'reader';

  // Collect the header buttons in one place so the empty-group guard and the
  // render conditions can't drift apart (adding a button = one array entry).
  const headerButtons = [
    downloadButtonProps && (
      <SecondaryButton key="download" onClick={downloadButtonProps.onClick}>
        {downloadButtonProps.label}
      </SecondaryButton>
    ),
    showCopyURLButton && copyUrlButtonProps && (
      <CopyEntityURLButton key="copyUrl" {...copyUrlButtonProps} />
    ),
  ].filter(isTruthy);

  // Render the group only when at least one button is present, otherwise an
  // empty IslandActionsButtonGroup container renders in the header.
  const buttons = headerButtons.length > 0 ? headerButtons : null;

  // Tracking labels are intentionally kept in English: they identify
  // analytics events, not user-facing copy.
  const trackEventExpandDescription = (isExpanded: boolean) =>
    trackEvent(
      DashboardTrackingEventsNames.CLICKED_EXPAND_DASHBOARD_DESCRIPTION_BUTTON,
      { clicked: !isExpanded ? 'Show less' : 'Show more' }
    );

  // In the core app reader, the copy URL button is shown in the page header
  const showExpandDescriptionButton = dashboardMode !== DashboardModes.METRICS;

  return {
    description: dashboardData.description,
    maxWidth: `${DASHBOARD_MAX_WIDTH}px`,
    buttons,
    trackEventExpandDescription,
    showExpandDescriptionButton,
    isDescriptionExpanded: isCaptureMode ? true : undefined,
  };
};

const Dashboard = ({
  dashboardData,
  isLoading,
  trackEvent,
  onWidgetDrilldown,
  apiError,
  dashboardMode = DashboardModes.VIEW,
  contextDashboardIsUsedIn,
  newFeatureNotifications = [],
  fullscreenWidgetProps,
  userLocale,
  copyUrlButtonProps,
  openFullscreenWidget,
  dashboardColors,
  dashboardFilterIslandProps,
  downloadButtonProps,
  i18nLocale,
  isCaptureMode = false,
  ...contextProps
}: DashboardProps & WithI18nLocale) => {
  const containerRef = useRef<HTMLDivElement>(null);

  if (apiError)
    return <DashboardErrorPage error={apiError} i18nLocale={i18nLocale} />;
  else if (isLoading || !dashboardData) {
    return <DashboardLoadingOverlay i18nLocale={i18nLocale} />;
  }

  const onDrilldown: OnWidgetDrilldown | undefined =
    dashboardMode !== DashboardModes.METRICS && onWidgetDrilldown
      ? ({ datasource, widgetType, filters }) => {
          trackDrilldown(
            trackEvent,
            contextDashboardIsUsedIn === 'presentation'
              ? DrilldownFromLocations.PRESENTATION
              : DrilldownFromLocations.DASHBOARD,
            widgetType as
              | 'number'
              | 'table'
              | 'pieChart'
              | 'stackedBarChart'
              | 'lineChart',
            filters
          );
          onWidgetDrilldown({
            datasource,
            filters,
            dashboardId: dashboardData._id,
          });
        }
      : undefined;

  const sortedWidgetPropsArray = getSortedWidgetPropsArray({
    dashboardData,
    openFullscreenWidget,
    contextDashboardIsUsedIn,
    dashboardMode,
    userLocale,
    colors: dashboardColors,
    onDrilldown,
    getReportNameById: contextProps.getReportNameById,
    getComponentLinkHandler: contextProps.getComponentLinkHandler,
    getHasComponentReadPermission: contextProps.getHasComponentReadPermission,
    i18nLocale,
  });

  const isEmptyDashboard = !dashboardData.widgets?.length;
  const isUsedInCoreAppReader = contextDashboardIsUsedIn === 'reader';

  const showHeaderIsland =
    (isUsedInCoreAppReader && dashboardData.description) || // only show description in the header island in the core app
    !isUsedInCoreAppReader;

  const pageHeaderIslandProps = getPageHeaderIslandProps({
    dashboardMode,
    dashboardData,
    contextDashboardIsUsedIn,
    copyUrlButtonProps,
    trackEvent,
    userLocale,
    downloadButtonProps,
    isCaptureMode,
  });

  // Not included in getPageHeaderIslandProps because it includes JSX
  const DashboardTitle =
    dashboardMode === DashboardModes.METRICS ? (
      <MetricsDateRangeTitle
        name={dashboardData.name}
        periodStart={dashboardData.periodStart!}
        periodEnd={dashboardData.periodEnd!}
        locale={userLocale}
      />
    ) : contextDashboardIsUsedIn !== 'reader' ? (
      dashboardData.name
    ) : null;

  return (
    <ChartAnimationDisabledContext.Provider value={isCaptureMode}>
      <Container ref={containerRef} $isCaptureMode={isCaptureMode}>
        <TimeStamp
          time={dashboardData._time}
          locale={userLocale}
          i18nLocale={i18nLocale}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxWidth: `${DASHBOARD_MAX_WIDTH}px`,
            width: '100%',
            height: '90%',
            gap: '16px',
          }}
        >
          {Boolean(newFeatureNotifications.length) && (
            <NotificationWrapper $maxWidth={DASHBOARD_MAX_WIDTH}>
              {newFeatureNotifications.map((notificationProps, i) => (
                <ActionableNewFeatureNotification
                  key={i}
                  {...notificationProps}
                />
              ))}
            </NotificationWrapper>
          )}

          {dashboardFilterIslandProps?.show && (
            <FlexBox width="full" padding="xsmall" justify="space-between">
              <DashboardFilterReaderIsland
                {...dashboardFilterIslandProps}
                i18nLocale={i18nLocale}
              />
            </FlexBox>
          )}

          {showHeaderIsland && (
            <PageHeaderIsland
              {...pageHeaderIslandProps}
              title={DashboardTitle}
            />
          )}
          {isEmptyDashboard ? (
            <EmptyDashboard
              dashboardMode={dashboardMode}
              i18nLocale={i18nLocale}
            />
          ) : (
            <WidgetsContainer>
              {sortedWidgetPropsArray.map(widgetProps =>
                isHeaderProps(widgetProps) ? (
                  <HeaderWidget
                    {...widgetProps}
                    key={widgetProps._id}
                    isInEditMode={false}
                    i18nLocale={i18nLocale}
                  />
                ) : isNoteProps(widgetProps) ? (
                  <NoteWidget
                    {...widgetProps}
                    key={widgetProps._id}
                    isInEditMode={false}
                    i18nLocale={i18nLocale}
                  />
                ) : isPowerBiIframeProps(widgetProps) ? (
                  <PowerBiIframeWidget
                    {...widgetProps}
                    key={widgetProps._id}
                    isInEditMode={false}
                    i18nLocale={i18nLocale}
                  />
                ) : isChartProps(widgetProps) ? (
                  <ChartWidget
                    {...widgetProps}
                    dashboardContainerRef={containerRef.current}
                    key={widgetProps._id}
                    i18nLocale={i18nLocale}
                  />
                ) : null
              )}
            </WidgetsContainer>
          )}
        </div>
      </Container>
      {dashboardMode === DashboardModes.VIEW && fullscreenWidgetProps && (
        <FullscreenWidgetModal
          dashboardContainerRef={containerRef.current}
          {...fullscreenWidgetProps}
        />
      )}
    </ChartAnimationDisabledContext.Provider>
  );
};

export { Dashboard };
